import { bff_prefix } from '@/consts';
import { forum, forumpost } from '@/typings/open.platform.forum_bff';
import { request } from '@/utils/request';

const prefix = `${bff_prefix}/forumPost`;
const formPrefix = `${bff_prefix}/forumservice`;

class Service {
  async deletePostGroup(req: forumpost.DeletePostGroupRequest) {
    const res = await request<
      forumpost.DeletePostGroupRequest,
      forumpost.DeletePostGroupResponseData
    >({
      url: `${prefix}/deletePostGroup`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async getBanners(req: forumpost.GetBannersRequest) {
    const res = await request<
      forumpost.GetBannersRequest,
      forumpost.GetBannersResponseData
    >({
      url: `${prefix}/getBanners`,
      params: req,
    });
    return res.data;
  }

  async getPostsById(req: forumpost.GetPostsFromPostIdRequest) {
    const res = await request<
      forumpost.GetPostsFromPostIdRequest,
      forumpost.GetPostsFromPostIdResponseData
    >({
      url: `${prefix}/getPostsFromPostId`,
      params: req,
    });
    return res.data;
  }

  async getPosts(req: forumpost.GetPostsRequest) {
    const res = await request<
      forumpost.GetPostsRequest,
      forumpost.GetPostsResponseData
    >({
      url: `${prefix}/getPosts`,
      params: req,
    });
    return res.data;
  }

  async toggleGood(req: forumpost.ToggleGoodRequest) {
    const res = await request<
      forumpost.ToggleGoodRequest,
      forumpost.ToggleGoodResponseData
    >({
      url: `${prefix}/toggleGood`,
      method: 'POST',
      data: req,
    });
    return res.data;
  }

  async toggleTop(req: forumpost.ToggleTopRequest) {
    const res = await request<
      forumpost.ToggleTopRequest,
      forumpost.ToggleTopResponseData
    >({
      url: `${prefix}/toggleTop`,
      method: 'POST',
      data: req,
    });
    return res.data;
  }

  async getPostsByGroup(req: forumpost.GetPostsByGroupRequest) {
    const res = await request<
      forumpost.GetPostsByGroupRequest,
      forumpost.GetPostsByGroupResponseData
    >({
      url: `${prefix}/getPostsByGroup`,
      params: req,
    });
    return res.data;
  }

  async deleteGroup(req: forumpost.DeleteGroupRequest) {
    const res = await request<
      forumpost.DeleteGroupRequest,
      forumpost.DeleteGroupResponseData
    >({
      url: `${prefix}/deleteGroup`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async removePost(req: forumpost.RemovePostRequest) {
    const res = await request<
      forumpost.RemovePostRequest,
      forumpost.RemovePostResponseData
    >({
      url: `${prefix}/removePost`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async insertPostGroup(req: forumpost.InsertPostGroupRequest) {
    const res = await request<
      forumpost.InsertPostGroupRequest,
      forumpost.InsertGroupResponseData
    >({
      url: `${prefix}/insertPostGroup`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async insertGroup(req: forumpost.InsertGroupRequest) {
    const res = await request<
      forumpost.InsertGroupRequest,
      forumpost.InsertGroupResponseData
    >({
      url: `${prefix}/insertGroup`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async createPost(req: forumpost.CreatePostRequest) {
    const res = await request<
      forumpost.CreatePostRequest,
      forumpost.CreatePostResponseData
    >({
      url: `${prefix}/createPost`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async selfVisiblePost(req: forumpost.SelfVisiblePostRequest) {
    const res = await request<
      forumpost.SelfVisiblePostRequest,
      forumpost.SelfVisiblePostResponseData
    >({
      url: `${prefix}/selfVisiblePost`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async visiblePost(req: forumpost.VisiblePostRequest) {
    const res = await request<
      forumpost.VisiblePostRequest,
      forumpost.VisiblePostResponseData
    >({
      url: `${prefix}/visiblePost`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async getPostsByGroupId(req: forumpost.GetPostsByGroupIdRequest) {
    const res = await request<
      forumpost.GetPostsByGroupIdRequest,
      forumpost.GetPostsByGroupIdResponseData
    >({
      url: `${prefix}/getPostsByGroupId`,
      params: req,
    });
    return res.data;
  }

  async esSearch(req: forumpost.EsSearchRequest) {
    const res = await request<
      forumpost.EsSearchRequest,
      forumpost.EsSearchResponseData
    >({
      url: `${prefix}/esSearch`,
      params: req,
    });
    return res.data;
  }

  async getPost(req: forumpost.GetPostRequest) {
    const res = await request<typeof req, forumpost.GetPostResponseData>({
      url: `${prefix}/getPost`,
      params: req,
    });
    return res.data;
  }

  async getReplyList(req: forum.GetRepliesRequest) {
    const res = await request<typeof req, forum.GetRepliesResponseData>({
      url: `${formPrefix}/getReplies`,
      params: req,
    });
    return res.data;
  }

  async getCommentList(req: forum.GetCommentsRequest) {
    const res = await request<typeof req, forum.GetCommentsResponseData>({
      url: `${formPrefix}/getComments`,
      params: req,
    });
    return res.data;
  }

  async createComment(req: forumpost.CreateCommentRequest) {
    const res = await request<typeof req, forumpost.CreateCommentResponseData>({
      url: `${prefix}/createComment`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async createReply(req: forumpost.CreateReplyRequest) {
    const res = await request<typeof req, forumpost.CreateReplyResponseData>({
      url: `${prefix}/createReply`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async deleteComment(req: forumpost.DeleteCommentRequest) {
    const res = await request<typeof req, forumpost.DeleteCommentResponseData>({
      url: `${prefix}/deleteComment`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async toggleCommentDelete(req: forumpost.ToggleCommentDeleteRequest) {
    const res = await request<
      typeof req,
      forumpost.ToggleCommentDeleteResponseData
    >({
      url: `${prefix}/toggleCommentDelete`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async deleteReply(req: forumpost.DeleteReplyRequest) {
    const res = await request<typeof req, forumpost.DeleteReplyResponseData>({
      url: `${prefix}/deleteReply`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async selfVisibleComment(req: forumpost.SelfVisibleCommentRequest) {
    const res = await request<
      typeof req,
      forumpost.SelfVisibleCommentResponseData
    >({
      url: `${prefix}/selfVisibleComment`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async selfVisibleReply(req: forumpost.SelfVisibleReplyRequest) {
    const res = await request<
      typeof req,
      forumpost.SelfVisibleReplyResponseData
    >({
      url: `${prefix}/selfVisibleReply`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async toggleLike(req: forumpost.ToggleLikeRequest) {
    const res = await request<typeof req, forumpost.ToggleLikeResponseData>({
      url: `${prefix}/toggleLike`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }

  async updatePost(req: forumpost.UpdatePostRequest) {
    const res = await request<typeof req, forumpost.UpdatePostResponseData>({
      url: `${prefix}/updatePost`,
      data: req,
      method: 'POST',
    });
    return res.data;
  }
}

export const forumPostService = new Service();
